import React, { useState, useEffect, useRef } from 'react';
import { CheckIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Button } from '../components/button';
import { Input } from '../components/input';
import { requestSubdomain, updateOwnBranding } from '../api/branding';
import { getBrandTextColors } from '../hooks/useBranding';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

const ConfiguracionMarca = ({ orgData, isOwner, brandingEnabled }) => {
  // Branding (white-label) editing states. Colors start EMPTY: empty means
  // "no override → use the Surelia look", which is what the preview shows.
  const brandingSeeded = useRef(false);
  const [brandingColorPrimario, setBrandingColorPrimario] = useState('');
  const [brandingColorSecundario, setBrandingColorSecundario] = useState('');
  const [brandingNombre, setBrandingNombre] = useState('');
  const [brandingLogo, setBrandingLogo] = useState(null);
  const [brandingLogoPreview, setBrandingLogoPreview] = useState(null);
  const [brandingLogoBgWhite, setBrandingLogoBgWhite] = useState(false);
  const [isSavingBranding, setIsSavingBranding] = useState(false);
  const [brandingMsg, setBrandingMsg] = useState('');
  const [brandingError, setBrandingError] = useState('');
  const [previewScreen, setPreviewScreen] = useState('login');

  // Subdomain request states
  const [isRequestingSubdomain, setIsRequestingSubdomain] = useState(false);
  const [subdomainError, setSubdomainError] = useState('');

  // Seed the branding form once orgData first arrives (never clobbers edits).
  useEffect(() => {
    if (!orgData || brandingSeeded.current) return;
    brandingSeeded.current = true;
    const b = orgData.branding || {};
    if (b.colorPrimario) setBrandingColorPrimario(b.colorPrimario);
    if (b.colorSecundario) setBrandingColorSecundario(b.colorSecundario);
    if (b.nombreComercial) setBrandingNombre(b.nombreComercial);
    setBrandingLogoBgWhite(b.logoBgWhite === true);
  }, [orgData]);

  const handleBrandingLogoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setBrandingLogo(file);
    setBrandingLogoPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleSaveBranding = async () => {
    setBrandingError('');
    setBrandingMsg('');
    if (brandingColorPrimario && !HEX_COLOR_RE.test(brandingColorPrimario)) {
      setBrandingError('El color primario debe tener formato #rrggbb.');
      return;
    }
    if (brandingColorSecundario && !HEX_COLOR_RE.test(brandingColorSecundario)) {
      setBrandingError('El color secundario debe tener formato #rrggbb.');
      return;
    }
    setIsSavingBranding(true);
    try {
      await updateOwnBranding({
        colorPrimario: brandingColorPrimario,
        colorSecundario: brandingColorSecundario,
        nombreComercial: brandingNombre,
        logo: brandingLogo,
      });
      // logoBgWhite is not part of the backend's branding form, so write it
      // directly (owners may update their org doc). Only when a logo exists —
      // never create a branding object out of this field alone — and after
      // the backend save, whose merge-from-existing preserves it later.
      const hadLogo = Boolean(orgData.branding?.logoUrl || brandingLogo);
      const savedLogoBgWhite = orgData.branding?.logoBgWhite === true;
      if (hadLogo && savedLogoBgWhite !== brandingLogoBgWhite) {
        await updateDoc(doc(db, 'orgs', orgData.id), {
          'branding.logoBgWhite': brandingLogoBgWhite,
        });
      }
      // The org doc's branding is written by the backend and arrives via the
      // live orgData listener, so the preview/logo refresh on their own.
      setBrandingMsg('Personalización guardada.');
      setBrandingLogo(null);
      setBrandingLogoPreview(null);
    } catch (error) {
      setBrandingError(error.message || 'No se pudo guardar la personalización.');
    } finally {
      setIsSavingBranding(false);
    }
  };

  const handleRemoveLogo = async () => {
    // If it's an unsaved selection, just clear it locally.
    if (brandingLogoPreview) {
      setBrandingLogo(null);
      setBrandingLogoPreview(null);
      return;
    }
    if (!window.confirm('¿Quitar el logo? Volverá a mostrarse el logo de Surelia.')) return;
    setBrandingError('');
    setBrandingMsg('');
    setIsSavingBranding(true);
    try {
      await updateOwnBranding({ removeLogo: true });
      setBrandingMsg('Logo eliminado.');
    } catch (error) {
      setBrandingError(error.message || 'No se pudo quitar el logo.');
    } finally {
      setIsSavingBranding(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Restablecer a la marca Surelia? Se quitarán tu logo y tus colores.')) return;
    setBrandingError('');
    setBrandingMsg('');
    setIsSavingBranding(true);
    try {
      await updateOwnBranding({ reset: true });
      setBrandingColorPrimario('');
      setBrandingColorSecundario('');
      setBrandingNombre('');
      setBrandingLogo(null);
      setBrandingLogoPreview(null);
      setBrandingLogoBgWhite(false);
      setBrandingMsg('Marca restablecida a Surelia.');
    } catch (error) {
      setBrandingError(error.message || 'No se pudo restablecer.');
    } finally {
      setIsSavingBranding(false);
    }
  };

  const handleRequestSubdomain = async () => {
    setSubdomainError('');
    setIsRequestingSubdomain(true);
    try {
      await requestSubdomain();
      // The org doc's `subdomainRequest` flag is written by the backend and
      // arrives via the live orgData listener, flipping the card to "pending".
    } catch (error) {
      setSubdomainError(error.message || 'No se pudo enviar la solicitud.');
    } finally {
      setIsRequestingSubdomain(false);
    }
  };

  if (!isOwner) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <Heading level={2}>Marca</Heading>
        <Text className="text-sm text-azul-surelia/70 mt-2">
          Solo los propietarios pueden personalizar la marca de la organización.
        </Text>
      </div>
    );
  }

  if (!orgData?.id) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <Text className="text-sm text-azul-surelia/70">Cargando...</Text>
      </div>
    );
  }

  if (!brandingEnabled) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <Heading level={2}>Marca</Heading>
        <Text className="text-sm text-azul-surelia/70 mt-2">
          La personalización no está habilitada para tu organización.
          Escríbenos para activarla.
        </Text>
      </div>
    );
  }

  // Effective values for the preview. Empty inputs → the current Surelia look.
  const SURELIA_LOGO = '/logo-blanco-verde.png';
  const validPrimary = HEX_COLOR_RE.test(brandingColorPrimario) ? brandingColorPrimario : null;
  const validSecondary = HEX_COLOR_RE.test(brandingColorSecundario) ? brandingColorSecundario : null;
  // One color is enough to preview: fill the missing side from the other, so
  // the gradient reacts as soon as you pick the FIRST color.
  const hasAnyColor = Boolean(validPrimary || validSecondary);
  const gradFrom = validPrimary || validSecondary;
  const gradTo = validSecondary || validPrimary;
  const previewLogo = brandingLogoPreview || orgData.branding?.logoUrl || SURELIA_LOGO;
  const loginGradient = hasAnyColor
    ? `linear-gradient(to bottom right, ${gradFrom}, ${gradTo})`
    : 'linear-gradient(to bottom right, #284865, rgba(40,72,101,0.85))';
  // Recepción documental: same two-color gradient as the login page, with the
  // heading color derived from the brand (same math as the real page).
  const recepHeadingColor = hasAnyColor
    ? (getBrandTextColors(gradFrom, gradTo)?.heading || 'rgba(255,255,255,0.95)')
    : '#d1d5db';
  const host = orgData.subdomain ? `${orgData.subdomain}.surelia.mx` : 'app.surelia.mx';
  const previewScreens = [
    { key: 'login', label: 'Inicio de sesión', path: '/login' },
    { key: 'recepcion', label: 'Recepción documental', path: '/expediente/…' },
    { key: 'panel', label: 'Panel', path: '/app/inicio' },
    { key: 'correo', label: 'Correo', path: '' },
  ];
  const activeScreen = previewScreens.find((s) => s.key === previewScreen) || previewScreens[0];
  // Correo de bienvenida: solid primario background, secundario accent —
  // same resolution as resolveEmailBranding in functions/index.js.
  const emailBg = hasAnyColor ? gradFrom : '#284865';
  const emailAccent = hasAnyColor ? gradTo : '#C2F40C';
  const yiqTextColor = (hex) => {
    const n = parseInt(hex.slice(1), 16);
    const yiq = (((n >> 16) & 255) * 299 + ((n >> 8) & 255) * 587 + (n & 255) * 114) / 1000;
    return yiq >= 140 ? '#284865' : '#ffffff';
  };

  return (
    <div className="space-y-6">
      {/* Personalización de marca */}
      <div className="bg-white rounded-lg shadow p-6">
        <Heading level={2} className="mb-2">Personalización de marca</Heading>
        <Text className="text-sm text-azul-surelia/70 mb-5">
          Sube tu logo y elige tus colores. Aparecerán en el inicio de sesión,
          el panel y la página de recepción de documentos de tus clientes.
        </Text>

        {/* Logo */}
        <div className="mb-5">
          <Text className="text-sm font-medium text-azul-surelia/80 mb-2">Logo</Text>
          <div className="flex items-center gap-4 flex-wrap">
            {(brandingLogoPreview || orgData.branding?.logoUrl) ? (
              <img
                src={brandingLogoPreview || orgData.branding.logoUrl}
                alt="Logo"
                className="h-12 w-auto rounded border border-gray-200 bg-gray-50 p-1"
              />
            ) : (
              <span className="text-sm text-gray-400">Sin logo (se usa Surelia)</span>
            )}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/avif"
              onChange={handleBrandingLogoChange}
              className="text-sm text-azul-surelia/80 file:mr-3 file:rounded-lg file:border-0 file:bg-azul-surelia/10 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-azul-surelia hover:file:bg-azul-surelia/20"
            />
            {(brandingLogoPreview || orgData.branding?.logoUrl) && (
              <button
                type="button"
                onClick={handleRemoveLogo}
                disabled={isSavingBranding}
                className="text-sm font-medium text-red-600 hover:text-red-700 hover:underline disabled:opacity-50"
              >
                {brandingLogoPreview ? 'Cancelar' : 'Quitar logo'}
              </button>
            )}
          </div>
          <Text className="text-xs text-azul-surelia/60 mt-1">
            Usa un <strong>PNG sin fondo</strong> (transparente) para que el logo se
            vea bien sobre cualquier color. Máximo 5MB.{' '}
            ¿No tienes uno sin fondo? Quítalo gratis en{' '}
            <a
              href="https://www.remove.bg/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-azul-surelia underline hover:text-azul-surelia/80"
            >
              remove.bg
            </a>{' '}
            o{' '}
            <a
              href="https://www.adobe.com/express/feature/image/remove-background"
              target="_blank"
              rel="noopener noreferrer"
              className="text-azul-surelia underline hover:text-azul-surelia/80"
            >
              Adobe Express
            </a>.
          </Text>
        </div>

        {/* Fondo del logo en el panel */}
        {(brandingLogoPreview || orgData.branding?.logoUrl) && (
          <div className="mb-5">
            <Text className="text-sm font-medium text-azul-surelia/80 mb-2">
              Fondo del logo en el panel
            </Text>
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
              {[
                { value: false, label: 'Azul Surelia' },
                { value: true, label: 'Blanco' },
              ].map((opt) => (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => setBrandingLogoBgWhite(opt.value)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    brandingLogoBgWhite === opt.value
                      ? 'bg-white text-azul-surelia shadow-sm'
                      : 'text-gray-500 hover:text-azul-surelia'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <Text className="text-xs text-azul-surelia/60 mt-1">
              Si tu logo es oscuro, elige fondo blanco para que se distinga en la
              barra lateral del panel.
            </Text>
          </div>
        )}

        {/* Colores */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div>
            <Text className="text-sm font-medium text-azul-surelia/80 mb-2">Color primario</Text>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={HEX_COLOR_RE.test(brandingColorPrimario) ? brandingColorPrimario : '#284865'}
                onChange={(e) => setBrandingColorPrimario(e.target.value)}
                className="h-9 w-12 cursor-pointer rounded border border-gray-200"
                aria-label="Color primario"
              />
              <Input
                value={brandingColorPrimario}
                onChange={(e) => setBrandingColorPrimario(e.target.value)}
                placeholder="#0184be"
                className="font-mono"
              />
            </div>
          </div>
          <div>
            <Text className="text-sm font-medium text-azul-surelia/80 mb-2">Color secundario</Text>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={HEX_COLOR_RE.test(brandingColorSecundario) ? brandingColorSecundario : '#53B5CC'}
                onChange={(e) => setBrandingColorSecundario(e.target.value)}
                className="h-9 w-12 cursor-pointer rounded border border-gray-200"
                aria-label="Color secundario"
              />
              <Input
                value={brandingColorSecundario}
                onChange={(e) => setBrandingColorSecundario(e.target.value)}
                placeholder="#016a98"
                className="font-mono"
              />
            </div>
          </div>
        </div>

        {/* Nombre comercial */}
        <div className="mb-5">
          <Text className="text-sm font-medium text-azul-surelia/80 mb-2">
            Nombre comercial (opcional)
          </Text>
          <Input
            value={brandingNombre}
            onChange={(e) => setBrandingNombre(e.target.value)}
            placeholder="Ej. Nombre de tu empresa"
            maxLength={80}
          />
        </div>

        {/* Vista previa multi-pantalla */}
        <div className="mb-5">
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <Text className="text-sm font-medium text-azul-surelia/80">Vista previa</Text>
            {/* Selector de pantalla */}
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
              {previewScreens.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setPreviewScreen(s.key)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    previewScreen === s.key
                      ? 'bg-white text-azul-surelia shadow-sm'
                      : 'text-gray-500 hover:text-azul-surelia'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Marco tipo navegador */}
          <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-100 px-3 py-2">
              <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
              <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
              <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
              <div className="ml-2 flex h-5 flex-1 items-center rounded bg-white px-2 text-[10px] text-gray-400">
                {previewScreen === 'correo'
                  ? 'De: Surelia <notificaciones@surelia.mx>'
                  : `${host}${activeScreen.path}`}
              </div>
            </div>

            {/* Pantalla */}
            <div className="h-[300px] overflow-hidden bg-white">
              {/* --- Inicio de sesión --- */}
              {previewScreen === 'login' && (
                <div
                  className="relative flex h-full flex-col items-center justify-center overflow-hidden px-6"
                  style={{ background: loginGradient }}
                >
                  <div className="pointer-events-none absolute inset-0 opacity-10">
                    <div className="absolute left-8 top-8 h-28 w-28 rounded-full bg-white blur-2xl" />
                    <div className="absolute bottom-8 right-8 h-24 w-24 rounded-full bg-white blur-2xl" />
                  </div>
                  <img src={previewLogo} alt="Logo" className="relative z-10 mb-5 h-12 w-auto" />
                  <div className="relative z-10 w-full max-w-[240px] rounded-xl bg-white/95 p-5 shadow-2xl">
                    <div className="mb-4 text-center text-sm font-semibold text-gray-700">
                      Acceso al Sistema
                    </div>
                    <div className="space-y-3">
                      <div>
                        <div className="mb-1 h-2 w-20 rounded bg-gray-300" />
                        <div className="h-7 rounded-md border border-gray-200 bg-gray-50" />
                      </div>
                      <div>
                        <div className="mb-1 h-2 w-16 rounded bg-gray-300" />
                        <div className="h-7 rounded-md border border-gray-200 bg-gray-50" />
                      </div>
                      <div
                        className="mt-1 flex h-7 items-center justify-center rounded-md text-[11px] font-medium text-white"
                        style={{ backgroundColor: '#284865' }}
                      >
                        Iniciar Sesión
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* --- Recepción documental --- */}
              {previewScreen === 'recepcion' && (
                <div className="h-full overflow-hidden p-4" style={{ background: loginGradient }}>
                  <img src={previewLogo} alt="Logo" className="mb-3 h-9 w-auto" />
                  <div className="mb-3 text-base font-semibold" style={{ color: recepHeadingColor }}>
                    Recepción de documentos: Empresa Demo
                  </div>
                  <div className="rounded-lg bg-white p-3 shadow">
                    <div className="grid grid-cols-3 gap-2">
                      {['Acta Constitutiva', 'Situación Fiscal', 'Identificación', 'Comprobante', 'Estados Fin.', 'Contrato'].map(
                        (label) => (
                          <div key={label} className="rounded-md border border-dashed border-gray-300 p-2">
                            <div className="mb-1 h-2 w-4/5 rounded bg-gray-300" />
                            <div className="text-[8px] leading-tight text-gray-500">{label}</div>
                            <div className="mt-1.5 flex h-6 items-center justify-center rounded bg-gray-50 text-[8px] text-gray-400">
                              Subir
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* --- Panel --- (sidebar azul-surelia, activo verde-lima,
                   fondo azul-surelia, tarjeta de contenido blanca) */}
              {previewScreen === 'panel' && (
                <div className="flex h-full p-2" style={{ backgroundColor: '#284865' }}>
                  {/* Sidebar */}
                  <div className="flex w-1/3 max-w-[150px] flex-col p-3">
                    <div className={`mb-4 flex h-8 items-center ${brandingLogoBgWhite ? 'rounded-md bg-white px-2' : ''}`}>
                      <img src={previewLogo} alt="Logo" className="h-7 w-auto max-w-full object-contain" />
                    </div>
                    <div className="space-y-1">
                      {/* Item activo: texto verde-lima con barra verde-lima */}
                      <div className="relative flex h-6 items-center rounded-md bg-white/10 px-2">
                        <span className="absolute inset-y-1 left-0 w-0.5 rounded-full" style={{ backgroundColor: '#C2F40C' }} />
                        <div className="h-1.5 w-16 rounded" style={{ backgroundColor: '#C2F40C' }} />
                      </div>
                      {[16, 12, 18, 14, 11].map((w, i) => (
                        <div key={i} className="flex h-6 items-center px-2">
                          <div className="h-1.5 rounded bg-white/40" style={{ width: `${w * 4}px` }} />
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Tarjeta de contenido blanca flotante */}
                  <div className="flex-1 rounded-lg bg-white p-4 shadow-sm ring-1 ring-white/10">
                    <div className="mb-3 h-3 w-32 rounded bg-gray-300" />
                    <div className="grid grid-cols-3 gap-2">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
                          <div className="mb-2 h-2 w-10 rounded bg-gray-200" />
                          <div className="h-4 w-8 rounded" style={{ backgroundColor: 'rgba(40,72,101,0.3)' }} />
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
                      <div className="mb-2 h-2 w-24 rounded bg-gray-200" />
                      <div className="space-y-1.5">
                        <div className="h-2 w-full rounded bg-gray-100" />
                        <div className="h-2 w-5/6 rounded bg-gray-100" />
                        <div className="h-2 w-4/6 rounded bg-gray-100" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* --- Correo de bienvenida --- */}
              {previewScreen === 'correo' && (
                <div
                  className="flex h-full items-start justify-center overflow-hidden py-4"
                  style={{ backgroundColor: emailBg }}
                >
                  <div className="w-[250px]">
                    <div className="mb-3 flex justify-center">
                      <img src={previewLogo} alt="Logo" className="h-8 w-auto" />
                    </div>
                    <div className="rounded-xl bg-white p-4 shadow-2xl">
                      <div className="mb-2 text-center text-[12px] font-semibold" style={{ color: '#284865' }}>
                        Tu cuenta está lista
                      </div>
                      <div className="mb-3 space-y-1">
                        <div className="h-1.5 w-full rounded bg-gray-200" />
                        <div className="h-1.5 w-5/6 rounded bg-gray-200" />
                      </div>
                      <div
                        className="mb-3 rounded-md bg-gray-50 p-2"
                        style={{ borderLeft: `3px solid ${emailAccent}` }}
                      >
                        <div className="text-[7px] uppercase tracking-wide text-gray-400">Tu cuenta</div>
                        <div className="text-[9px] font-semibold" style={{ color: '#284865' }}>
                          usuario@empresa.com
                        </div>
                        <div className="text-[8px] text-gray-500">Rol: Cliente</div>
                      </div>
                      <div
                        className="flex h-7 items-center justify-center rounded-md text-[10px] font-bold"
                        style={{ backgroundColor: emailAccent, color: yiqTextColor(emailAccent) }}
                      >
                        Establecer contraseña
                      </div>
                    </div>
                    <div
                      className="mt-3 text-center text-[9px] font-medium"
                      style={{ color: yiqTextColor(emailBg) }}
                    >
                      El equipo de Surelia
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="mt-2 space-y-1">
            {previewScreen === 'correo' ? (
              <Text className="text-xs text-azul-surelia/60">
                El correo de bienvenida a los usuarios que invitas usa tus
                colores y tu logo.
              </Text>
            ) : previewScreen === 'recepcion' ? (
              <Text className="text-xs text-azul-surelia/60">
                Tus clientes siempre ven tu marca en esta pantalla, con o sin subdominio.
                El título de la pestaña usa tu nombre comercial.
              </Text>
            ) : previewScreen === 'panel' ? (
              <Text className="text-xs text-azul-surelia/60">
                El logo aparece en el panel en cuanto tu equipo inicia sesión,
                con o sin subdominio.
              </Text>
            ) : (
              <Text className="text-xs text-azul-surelia/60">
                El inicio de sesión muestra tu marca solo si tu equipo entra por
                tu subdominio
                (<span className="font-mono">tu-empresa.surelia.mx</span>).
                En app.surelia.mx se ve la marca Surelia.
              </Text>
            )}
            {!hasAnyColor && !brandingLogoPreview && !orgData.branding?.logoUrl && (
              <Text className="text-xs text-azul-surelia/50">
                Sin cambios: así se ve hoy con la marca Surelia.
              </Text>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Button color="surelia" onClick={handleSaveBranding} disabled={isSavingBranding}>
            {isSavingBranding ? 'Guardando...' : 'Guardar personalización'}
          </Button>
          {(orgData.branding || brandingLogoPreview) && (
            <button
              type="button"
              onClick={handleReset}
              disabled={isSavingBranding}
              className="text-sm font-medium text-azul-surelia/70 hover:text-azul-surelia hover:underline disabled:opacity-50"
            >
              Restablecer a Surelia
            </button>
          )}
          {brandingMsg && <Text className="text-sm text-green-600">{brandingMsg}</Text>}
          {brandingError && <Text className="text-sm text-red-600">{brandingError}</Text>}
        </div>
      </div>

      {/* Subdominio personalizado */}
      <div className="bg-white rounded-lg shadow p-6">
        <Heading level={2} className="mb-2">Subdominio personalizado</Heading>
        <Text className="text-sm text-azul-surelia/70 mb-4">
          Un subdominio es una dirección web propia para tu organización, con la
          forma <code className="font-mono text-azul-surelia/80">tu-empresa.surelia.mx</code>.
          Tu equipo y tus clientes entran desde ahí y ven tu marca (logo y colores)
          desde el inicio de sesión.
        </Text>
        {orgData.subdomain ? (
          <div className="flex items-center gap-2">
            <CheckIcon className="w-5 h-5 text-green-600 shrink-0" />
            <Text className="text-sm text-azul-surelia/80">
              Subdominio activo:{' '}
              <code className="font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-700">
                {orgData.subdomain}.surelia.mx
              </code>
            </Text>
          </div>
        ) : orgData.subdomainRequest?.status === 'solicitado' ? (
          <>
            <div className="flex items-center gap-2 mb-1">
              <ClockIcon className="w-5 h-5 text-amber-500 shrink-0" />
              <Text className="text-sm text-azul-surelia/80">
                Solicitud enviada. El equipo de Surelia la revisará y te contactará.
              </Text>
            </div>
            <Button color="surelia" disabled className="mt-3">
              Solicitud enviada
            </Button>
          </>
        ) : (
          <>
            <Text className="text-sm text-azul-surelia/70 mb-4">
              El tuyo sería <code className="font-mono text-azul-surelia/80">{orgData.id}.surelia.mx</code>.
              Al solicitarlo, el equipo de Surelia lo configura por ti y te avisa cuando esté listo.
            </Text>
            <Button
              color="surelia"
              onClick={handleRequestSubdomain}
              disabled={isRequestingSubdomain}
            >
              {isRequestingSubdomain ? 'Enviando...' : 'Solicitar subdominio'}
            </Button>
            {subdomainError && (
              <Text className="text-sm text-red-600 mt-3">{subdomainError}</Text>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ConfiguracionMarca;
